import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Plus, XCircle } from "lucide-react";

/* Shared source-field → SalesOS-field mapper, used by both the Meta lead-form
   mapper and the Google Sheets column mapper — one mapping convention, one
   UI. Lists REAL existing custom fields (contact- and opportunity-entity) as
   selectable targets alongside a small fixed set of universal fields, plus
   "Preserve only / do not map". Never auto-selects anything — the admin
   picks every mapping explicitly. Supports inline create / edit / soft-delete
   of custom fields without leaving the mapper.

   Props:
   - rows: [{ key, label, sample, target }] — target is a fixed key, or
     "custom:<fieldKey>:<entity>", or "skip"
   - onChange(rows)
   - productCode: string | null — scopes which product-specific fields show
   - universalTargets: [[value, label], ...] — caller-specific fixed targets
     (e.g. name/phone/email for leads; a different set for other sources) */
export const FieldMapper = ({ rows, onChange, productCode, universalTargets }) => {
  const [fields, setFields] = useState([]);
  // Separate from `fields` (active-only, used as mapping targets below) —
  // this is the full admin management list (active AND deactivated), so a
  // previously soft-deleted field can still be found and, once unreferenced,
  // permanently deleted (P0 #4) — the active-only list has no way to
  // surface it at all once deactivated.
  const [allFields, setAllFields] = useState([]);
  const [editing, setEditing] = useState(null); // field_id being edited, or "new"
  const [draft, setDraft] = useState({ label: "", type: "text", entity: "contact", options: "" });
  const [creatingForRow, setCreatingForRow] = useState(null);

  const load = () => {
    const qs = productCode ? `?product_code=${productCode}` : "";
    api.get(`/custom-fields${qs}`).then((r) => setFields(r.data));
    api.get("/custom-fields/all").then((r) => setAllFields(r.data)).catch(() => setAllFields([]));
  };
  useEffect(load, [productCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRow = (i, patch) => {
    const next = [...rows];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };

  const startCreate = (rowIdx) => {
    setDraft({ label: rows[rowIdx]?.label || rows[rowIdx]?.key || "", type: "text", entity: "contact", options: "" });
    setCreatingForRow(rowIdx);
    setEditing("new");
  };

  const saveField = async () => {
    try {
      const body = {
        label: draft.label, type: draft.type, entity: draft.entity,
        section: draft.section || "Lead Data",
        product_code: productCode || null,
        options: draft.type === "dropdown" || draft.type === "multiselect"
          ? draft.options.split(",").map((s) => s.trim()).filter(Boolean) : null,
      };
      if (editing === "new") {
        const key = (draft.label || "field").toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 30);
        const r = await api.post("/custom-fields", { ...body, key });
        toast.success(`Custom field '${draft.label}' created`);
        if (creatingForRow !== null) setRow(creatingForRow, { target: `custom:${r.data.key}:${draft.entity}` });
      } else {
        await api.patch(`/custom-fields/${editing}`, body);
        toast.success("Field updated");
      }
      setEditing(null); setCreatingForRow(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const startEdit = (f) => {
    setDraft({ label: f.label, type: f.type, entity: f.entity, section: f.section,
              options: (f.options || []).join(", ") });
    setEditing(f.field_id);
  };

  const deleteField = async (f) => {
    try {
      await api.delete(`/custom-fields/${f.field_id}`);
      toast.success(`'${f.label}' deactivated — removed from future mapping, existing data untouched`);
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const deleteFieldPermanent = async (f) => {
    try {
      await api.delete(`/custom-fields/${f.field_id}/permanent`);
      toast.success(`'${f.label}' permanently deleted`);
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const visibleAllFields = productCode
    ? allFields.filter((f) => [null, productCode].includes(f.product_code))
    : allFields;

  const targetOptions = [
    ...(universalTargets || []),
    ...fields.map((f) => [`custom:${f.key}:${f.entity}`,
      `${f.label} (${f.entity === "contact" ? "Profile" : "This product"}${f.product_code ? "" : ", universal"})`]),
    ["skip", "— Preserve only / do not map —"],
  ];

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={r.key} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid={`fm-row-${r.key}`}>
            <div className="text-xs min-w-0">
              <div className="font-mono font-medium truncate" title={r.label || r.key}>{r.label || r.key}</div>
              {r.sample && <div className="text-muted-foreground truncate">e.g. {String(r.sample)}</div>}
            </div>
            <Select value={r.target || "skip"} onValueChange={(v) => setRow(i, { target: v })}>
              <SelectTrigger data-testid={`fm-target-${r.key}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {targetOptions.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-7 text-[11px]"
              data-testid={`fm-createcf-${r.key}`} onClick={() => startCreate(i)}>
              <Plus className="h-3 w-3 mr-1" />Field
            </Button>
          </div>
        ))}
      </div>

      <div className="border rounded-md p-2 space-y-1.5">
        <p className="text-[11px] font-medium text-muted-foreground uppercase">Existing custom fields{productCode ? ` — ${productCode} & universal` : ""}</p>
        {visibleAllFields.length === 0 && <p className="text-xs text-muted-foreground">None yet — use "+ Field" on a row above to create one.</p>}
        {visibleAllFields.map((f) => (
          <div key={f.field_id} className="flex items-center justify-between gap-2 text-xs py-0.5" data-testid={`fm-existing-${f.key}`}>
            <span className="flex items-center gap-1.5">
              {f.label} <span className="text-muted-foreground font-mono">({f.entity}, {f.type})</span>
              {f.active === false && <Badge variant="outline" className="text-[9px]">Inactive</Badge>}
            </span>
            <div className="flex items-center gap-1">
              {f.active !== false ? (
                <>
                  <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => startEdit(f)} data-testid={`fm-edit-${f.key}`}><Pencil className="h-3 w-3" /></button>
                  <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => deleteField(f)} data-testid={`fm-delete-${f.key}`} title="Deactivate — hides from future mapping, keeps existing data"><Trash2 className="h-3 w-3" /></button>
                </>
              ) : (
                <button type="button"
                  className={f.reference_reason ? "text-muted-foreground/40 cursor-not-allowed" : "text-muted-foreground hover:text-destructive"}
                  disabled={!!f.reference_reason}
                  title={f.reference_reason ? `Cannot permanently delete — ${f.reference_reason}` : "Delete permanently — irreversible, only possible because nothing references this field"}
                  onClick={() => deleteFieldPermanent(f)} data-testid={`fm-delete-permanent-${f.key}`}>
                  <XCircle className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="border rounded-md p-3 space-y-2 bg-muted/30">
          <p className="text-xs font-medium">{editing === "new" ? "New custom field" : "Edit custom field"}</p>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Label" className="h-8 text-xs" value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
            <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["text", "longtext", "number", "currency", "date", "boolean", "dropdown", "multiselect", "country"].map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={draft.entity} onValueChange={(v) => setDraft({ ...draft, entity: v })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="contact">Profile (contact-level)</SelectItem>
                <SelectItem value="opportunity">This product only (opportunity-level)</SelectItem>
              </SelectContent>
            </Select>
            {(draft.type === "dropdown" || draft.type === "multiselect") && (
              <Input placeholder="Options, comma separated" className="h-8 text-xs"
                value={draft.options} onChange={(e) => setDraft({ ...draft, options: e.target.value })} />
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setEditing(null); setCreatingForRow(null); }}>Cancel</Button>
            <Button size="sm" className="h-7 text-xs" data-testid="fm-field-save" onClick={saveField}>Save field</Button>
          </div>
        </div>
      )}
    </div>
  );
};
